import { type ChangeEvent, type FC, memo, useCallback, useEffect, useMemo, useRef } from "react";
import DateInput from "~/common/components/DateInput";
import CartQuantityInput from "~/features/cart/activeQuote/components/productTable/CartQuantityInput";
import { type PreviewItem, type PreviewItemUpdateData } from "~/features/bulkAddToCart/types/models";
import PreviewTableMaterialIdInput from "~/features/bulkAddToCart/components/PreviewTable/PreviewTableMaterialIdInput";
import PriceReferenceDropdown from "~/features/productDetails/components/AddToCartCard/PriceReferenceDropdown";
import Parameter from "~/common/components/Parameter";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { type ProductData, ProductMarketType } from "~/domains/product/types/ProductData";
import { isDateInvalidForDelivery } from "~/common/utils/dateUtils";

import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { iconClickablePrimarySx } from "~/common/components/icon/iconClickableSx";
import { Trans, useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { Box, IconButton, InputAdornment, Stack, styled, TextField, Typography } from "~/common/mui";
import { Link } from "~/common/components/Link";
import CommentsDisabledOutlinedIcon from "@mui/icons-material/CommentsDisabledOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { C_ERROR, C_INFORMATION } from "~/common/components/icon/Colors";
import { Alert } from "~/common/mui/Alert/Alert";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import CustomNumberFormat, { type NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import ClearIcon from "@mui/icons-material/Clear";

interface Props {
  index: number;
  data: PreviewItem;
  onDataChange: (id: number, newData: Partial<PreviewItemUpdateData>) => void;
  onRemoveItem: (id: number) => void;
  onSetCommentVisibility: (id: number, visible: boolean) => void;
  showPriceReference: boolean;
  targetPriceEnabled: boolean;
  onRender?: (element: Element) => void;
}

const PreviewTableRowWrapper: FC<Props & JSX.IntrinsicElements["div"]> = (props) => {
  const {
    index,
    data,
    onDataChange,
    onRemoveItem,
    onSetCommentVisibility,
    className,
    showPriceReference,
    targetPriceEnabled,
    onRender,
    ...divProps
  } = props;

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onRender && rootRef.current) {
      window.setTimeout(() => {
        if (onRender && rootRef.current) {
          onRender(rootRef.current);
        }
      }, 16);
    }
  }, [onRender, index, data]);

  return (
    <Box {...divProps} ref={rootRef} data-index={index}>
      <PreviewTableRow
        index={index}
        data={data}
        onDataChange={onDataChange}
        onRemoveItem={onRemoveItem}
        onSetCommentVisibility={onSetCommentVisibility}
        showPriceReference={showPriceReference}
        targetPriceEnabled={targetPriceEnabled}
      />
    </Box>
  );
};

let PreviewTableRow: FC<Omit<Props, "onRender">> = ({
  data,
  index,
  onDataChange,
  onRemoveItem,
  onSetCommentVisibility,
  showPriceReference,
  targetPriceEnabled,
}) => {
  const { t } = useTranslation();
  const id = data.id;
  const [
    onQtyChange,
    onIdChange,
    onDateChange,
    onCustomOrderChange,
    updatePriceReference,
    onCommentChange,
    setCommentVisibility,
    removeItem,
  ] = useMemo(() => {
    return [
      (newValue: number) => onDataChange(id, { quantity: newValue }),
      (newProduct: ProductData, inputText: string) =>
        onDataChange(id, {
          designation: newProduct.designation,
          sapMaterialNumber: newProduct.sapMaterialNumber,
          inputText,
          marketType: newProduct.marketType,
        }),
      (newDate: Date) => onDataChange(id, { requestedDeliveryDate: newDate }),
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onDataChange(id, { customerOrderNumber: e.target.value }),
      (priceReference: string | undefined) => onDataChange(id, { priceReference }),
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onDataChange(id, { comment: e.target.value }),
      (showComment: boolean) => onSetCommentVisibility(id, showComment),
      () => onRemoveItem(id),
    ];
  }, [id, onDataChange, onRemoveItem, onSetCommentVisibility]);

  const onTargetPriceChange = useCallback(
    (evt: NumberFormatEvent) => {
      onDataChange(id, { targetPrice: evt.floatValue });
    },
    [id, onDataChange],
  );

  useEffect(() => {
    if (window.SCHA.config.hasEndCustomer) {
      updatePriceReference(window.SCHA.config.priceReferences[1].value);
    }
  }, []);

  const { isCommentVisible, messages } = data;
  const housingProduct = data.housingProduct;

  const canMakeComments = canUseCommentsForProduct(data.marketType);

  const wasDateChanged = messages.some(
    (msg) =>
      msg.code === "bulkUpload.error.product.rdd.invalid" || msg.code === "bulkUpload.error.product.rdd.adjusted",
  );
  const isDateInvalid = messages.some((msg) => msg.code === "bulkUpload.error.product.rdd.tooFar");
  const onSale = data.onSale;

  const productError = messages.find((msg) => msg.code === "bulkUpload.error.product.id.invalid");
  const quantityError = messages.find((msg) => msg.code === "bulkUpload.error.product.quantity.invalid");
  const targetPriceError = messages.find((msg) => msg.code === "bulkUpload.error.targetPrice.invalid");
  const dateError = messages.find((msg) => {
    if (
      [
        "bulkUpload.error.product.rdd.invalid",
        "bulkUpload.error.product.rdd.tooFar",
        "bulkUpload.error.product.rdd.adjusted",
      ].includes(msg.code)
    ) {
      return msg;
    }
  });
  const priceReferenceError = messages.find((msg) => msg.code === "bulkUpload.error.product.reference.invalid");
  const productEntriesAdjustedInfo = messages.find((msg) => msg.code === "bulkUpload.error.product.info.adjusted");

  const hasInvalidTargetPrice =
    targetPriceEnabled &&
    !housingProduct &&
    data.targetPrice !== undefined &&
    data.targetPrice !== null &&
    data.targetPrice <= 0;

  const targetPriceErrorCode =
    targetPriceError?.code ?? (hasInvalidTargetPrice ? "bulkUpload.error.targetPrice.invalid" : undefined);
  const isTargetPriceInvalid = !!targetPriceErrorCode;
  const targetPriceHelperText = targetPriceErrorCode ? t(targetPriceErrorCode) : null;

  const clearComment = useCallback(() => {
    onDataChange(id, { comment: "" });
  }, [id, onDataChange]);

  return (
    <>
      <BulkHeaderTableData showPriceReferenceColumn={showPriceReference} targetPriceEnabled={targetPriceEnabled}>
        <Box color="text.secondary">{index + 1}</Box>
        <Box>
          <ProductAutoComplete
            key={data.productId}
            label={t("bulkUpload.table.header.productId")}
            value={data.productId}
            onChange={onIdChange}
            disableNewVerLink={true}
            variant={"outlined"}
            error={productError ? true : false}
            helperText={
              productError
                ? t(productError.code)
                : data.succeedingProductDesignation &&
                  !isPhaseOut2Enabled() && (
                    <Trans i18nKey={"product.hasNewerVersion"}>
                      newer version is existing{" "}
                      <Link
                        style={{ color: "inherit", fontWeight: "600" }}
                        className={"d-inline"}
                        onClick={() =>
                          onDataChange(id, {
                            productId: data.succeedingProductDesignation,
                            inputText: data.succeedingProductDesignation,
                            previousProductId: data.productId,
                            succeedingProductDesignation: "",
                          })
                        }
                      >
                        {{ name: data.succeedingProductDesignation }}
                      </Link>
                    </Trans>
                  )
            }
            color={productError ? "error" : data.succeedingProductDesignation ? "info" : undefined}
          />
          {isLineLevelCustomerOrderNumberEnabled() && (
            <Stack mt={2}>
              <TextField
                fullWidth
                variant={"outlined"}
                label={t("search.type.customerOrderNumber")}
                value={data.customerOrderNumber}
                onChange={onCustomOrderChange}
              />
            </Stack>
          )}
        </Box>
        <Box>
          {data.quantityVisible ? (
            <QuantityInput
              data-test-id={"bulk-upload-item-quantity"}
              label={t("cartPage.table.header.quantity.title")}
              onChange={onQtyChange}
              hideButtons={true}
              value={data.quantity}
              error={quantityError ? true : false}
              helperLabel={quantityError && t(quantityError.code)}
            />
          ) : (
            <Parameter label={t("input.quantity")} value={data.quantity} />
          )}
        </Box>
        <Box>
          <DeliveryDateInput
            label={t("cartPage.table.content.chooseDate")}
            onChange={onDateChange}
            data-test-id={"bulk-upload-item-date-selector-rdd"}
            date={data.requestedDeliveryDate ?? null}
            isHighlight={wasDateChanged} // info
            isInvalid={isDateInvalid} // error
            isDayDisabled={isDateInvalidForDelivery}
            helperLabel={dateError && t(dateError.code)}
            variant={"outlined"}
            iconClassName={undefined}
          />
        </Box>

        {!onSale && showPriceReference && (
          <Box className="price_reference">
            <PriceReferenceDropdown
              data-test-id={"bulk-upload-item-price-reference"}
              selected={data.priceReference}
              onChange={updatePriceReference}
              highlight={!!priceReferenceError}
              helperLabel={priceReferenceError && t(priceReferenceError.code)}
              size={"medium"}
            />
          </Box>
        )}

        {onSale && <OnSaleIndicator />}
        {/* Adding blank div to maintain the bulk upload popup design for onSale product */}
        {(housingProduct || onSale) && targetPriceEnabled && <Box className="bulk_preview_tp"></Box>}
        {/* Target Price component */}
        {!onSale && !housingProduct && targetPriceEnabled && (
          <Box>
            <TargetPrice
              data-test-id={"bulk-upload-item-targetPrice"}
              value={data.targetPrice ?? undefined}
              label={t("cartPage.table.header.targetPrice.title")}
              handleState={onTargetPriceChange}
              error={isTargetPriceInvalid}
              helperText={targetPriceHelperText}
            />
          </Box>
        )}

        <Stack flexDirection={"row"} gap={1}>
          {canMakeComments &&
            (isCommentVisible ? (
              <CommentsDisabledOutlinedIcon
                data-test-id={"bulk-upload-item-btn-toggle-comment"}
                sx={iconClickablePrimarySx}
                onClick={() => setCommentVisibility(!isCommentVisible)}
                fontSize="medium"
              />
            ) : (
              <CommentOutlinedIcon
                data-test-id={"bulk-upload-item-btn-toggle-comment"}
                sx={iconClickablePrimarySx}
                onClick={() => setCommentVisibility(!isCommentVisible)}
                fontSize="medium"
              />
            ))}
          <DeleteOutlineOutlinedIcon
            data-test-id={"bulk-upload-item-btn-remove"}
            sx={{ ...iconClickablePrimarySx, ml: canMakeComments ? 0 : 4 }}
            onClick={removeItem}
            fontSize="medium"
          />
        </Stack>
        {/* Comment Section */}
        {(isCommentVisible || productEntriesAdjustedInfo) && (
          <Box className="comment-section">
            {isCommentVisible && (
              <Stack>
                <TextField
                  variant={"outlined"}
                  label="Comment"
                  fullWidth
                  data-test-id={"bulk-upload-item-input-comment"}
                  helperText={t("bulkUpload.comment.limit")}
                  onChange={onCommentChange}
                  value={data.comment}
                  InputProps={{
                    endAdornment: data.comment ? (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={clearComment}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Stack>
            )}
            <MessageBox>
              {productEntriesAdjustedInfo && <Alert severity="info">{t(productEntriesAdjustedInfo.code)}</Alert>}
              {data.marketType === ProductMarketType.PMA && <InfoProductMessagePMA />}
              {/* Hide this for now in SCHAEFF-33406 */}
              {/* {data.previousProductId && (
            <UndoButton
              onClick={() =>
                onDataChange(id, {
                  productId: data.previousProductId,
                  inputText: data.previousProductId,
                })
              }
            />
          )} */}
            </MessageBox>
          </Box>
        )}
      </BulkHeaderTableData>
    </>
  );
};
PreviewTableRow = memo(PreviewTableRow);

export default PreviewTableRowWrapper;

const QuantityInput = styled(CartQuantityInput)({
  ".MuiFormHelperText-root": {
    fontSize: "12px",
  },
});

const TargetPrice = styled(CustomNumberFormat)({
  ".MuiFormHelperText-root": {
    fontSize: "12px",
  },
});

const ProductAutoComplete = styled(PreviewTableMaterialIdInput)(({ error, color }) => {
  const isHighlight = color === "info";
  const borderColor = isHighlight ? `${C_INFORMATION} !important` : error && `${C_ERROR} !important`;
  return {
    ".MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75) !important",
    },
    ".MuiOutlinedInput-root": {
      padding: "8px !important",
      paddingLeft: "16px !important",
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
    },
    ".MuiFormLabel-colorInfo": {
      color: C_INFORMATION,
      "&.Mui-focused": {
        color: `${C_INFORMATION} !important`,
      },
    },
    ".MuiFormHelperText-root": {
      fontSize: "12px",
      color: isHighlight && `${C_INFORMATION} !important`,
      marginTop: "4px",
      marginRight: "16px",
      marginLeft: "16px",
    },
  };
});

const DeliveryDateInput = styled(DateInput)(({ isInvalid, isHighlight }) => {
  const borderColor = isHighlight ? `${C_INFORMATION} !important` : isInvalid && `${C_ERROR} !important`;
  return {
    ".MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75) !important",
    },
    ".MuiOutlinedInput-root": {
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
    },
    ".MuiFormLabel-colorInfo": {
      color: C_INFORMATION,
      "&.Mui-focused": {
        color: `${C_INFORMATION} !important`,
      },
    },
    ".MuiFormHelperText-root": {
      fontSize: "12px",
      color: borderColor,
    },
  };
});

const OnSaleIndicator = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ height: "58px" }}>
      <SaleTag size={32} />
      <Typography color="salesCampaign" variant="bodyLarge">
        {t("feature.bulkEntry.tag.onSale")}
      </Typography>
    </Stack>
  );
};

const MessageBox = styled("div")(({ theme: _theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginTop: "16px",
  paddingInline: "20px",
  "&:empty": { margin: "0 !important" },
}));

export const BulkHeaderTableData = styled("div", {
  shouldForwardProp: (prop) => prop !== "showPriceReferenceColumn" && prop !== "targetPriceEnabled",
})<{
  showPriceReferenceColumn: boolean;
  targetPriceEnabled: boolean;
}>(({ theme, showPriceReferenceColumn, targetPriceEnabled }) => {
  return {
    borderBottom: `1px solid ${theme.palette?.divider}`,
    display: "grid",

    gridTemplateColumns:
      showPriceReferenceColumn && targetPriceEnabled
        ? "32px 200px 136px 200px 264px 135px 56px"
        : showPriceReferenceColumn || targetPriceEnabled
        ? "32px 272px 136px 272px 270px 56px"
        : "32px 322px 322px 323px 56px",

    gap: "16px",
    alignItems: "start",
    padding: "16px",

    "& .comment-section": {
      gridColumn: "2 / -1",
    },
  };
});
